import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { COMPANY } from "@/lib/company";

const YEAR = new Date().getFullYear();

export default function Footer() {
    return (
        <footer
            className="relative isolate overflow-hidden bg-brand-charcoal text-brand-offwhite"
            data-testid="site-footer"
        >
            <div className="container-x grid gap-14 py-20 md:grid-cols-12">
                <div className="md:col-span-5">
                    <img
                        src="/brand/logo-horizontal-dark.png?v=3"
                        alt="EB Construction — ElevateBuild Construction Services Ltd"
                        className="h-12 w-auto md:h-14"
                    />
                    <p
                        className="mt-4 max-w-md font-body text-sm italic leading-relaxed text-brand-offwhite/70"
                        data-testid="footer-tagline"
                    >
                        {COMPANY.tagline}
                    </p>
                    <p className="mt-8 font-mono-tag text-brand-gold">CONTACT</p>
                    <h3 className="mt-4 font-display text-4xl md:text-5xl">
                        LET'S BUILD SOMETHING <br />
                        <span className="text-brand-gold">SIGNIFICANT.</span>
                    </h3>
                    <Link
                        to="/contact"
                        className="mt-8 inline-flex items-center gap-2 border border-brand-gold bg-brand-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal transition-colors hover:bg-transparent hover:text-brand-gold"
                        data-testid="footer-cta-button"
                    >
                        Request a Quote
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="md:col-span-3">
                    <p className="font-mono-tag text-brand-gold">NAVIGATE</p>
                    <ul className="mt-4 flex flex-col gap-3">
                        {[
                            { to: "/", label: "Home" },
                            { to: "/about", label: "About" },
                            { to: "/services", label: "Services" },
                            { to: "/projects", label: "Projects" },
                            { to: "/careers", label: "Careers" },
                            { to: "/contact", label: "Contact" },
                            { to: "/portal", label: "Training Portal" },
                        ].map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="link-underline font-body text-sm text-brand-offwhite/80 hover:text-brand-gold"
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <p className="font-mono-tag text-brand-gold">OFFICE</p>
                    <ul className="mt-4 flex flex-col gap-4 text-sm text-brand-offwhite/85">
                        <li className="flex gap-3">
                            <MapPin className="h-4 w-4 flex-shrink-0 translate-y-1 text-brand-gold" />
                            <span data-testid="footer-address">
                                {COMPANY.address.line1}
                                <br />
                                {COMPANY.address.city}, {COMPANY.address.region}, {COMPANY.address.postcode}
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <Phone className="h-4 w-4 flex-shrink-0 translate-y-1 text-brand-gold" />
                            <a
                                href={COMPANY.phoneHref}
                                className="link-underline"
                                data-testid="footer-phone"
                            >
                                {COMPANY.phone}
                            </a>
                        </li>
                        <li className="flex gap-3">
                            <Mail className="h-4 w-4 flex-shrink-0 translate-y-1 text-brand-gold" />
                            <a
                                href={COMPANY.emailHref}
                                className="link-underline"
                                data-testid="footer-email"
                            >
                                {COMPANY.email}
                            </a>
                        </li>
                        <li className="flex gap-3">
                            <Clock className="h-4 w-4 flex-shrink-0 translate-y-1 text-brand-gold" />
                            <span data-testid="footer-hours">
                                {COMPANY.hours}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-brand-linelight">
                <div className="container-x flex flex-col items-start justify-between gap-6 py-8 md:flex-row md:items-center">
                    <div className="flex flex-col gap-2">
                        <p
                            className="font-mono-tag text-[10px] text-brand-offwhite/60"
                            data-testid="footer-copyright"
                        >
                            © {YEAR} {COMPANY.legalName.toUpperCase()} · ALL RIGHTS RESERVED
                        </p>
                        <p
                            className="font-body text-[11px] text-brand-offwhite/50"
                            data-testid="footer-legal-line"
                        >
                            {COMPANY.legalLine}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {[
                            { href: "#", label: "LinkedIn", Icon: Linkedin },
                            { href: "#", label: "Instagram", Icon: Instagram },
                            { href: "#", label: "Facebook", Icon: Facebook },
                        ].map(({ href, label, Icon }) => (
                            <a
                                key={label}
                                href={href}
                                aria-label={label}
                                className="grid h-10 w-10 place-items-center border border-brand-linelight text-brand-offwhite/80 transition-colors hover:border-brand-gold hover:text-brand-gold"
                                data-testid={`social-${label.toLowerCase()}`}
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                        <span
                            aria-hidden="true"
                            className="ml-2 hidden h-4 w-px bg-brand-linelight md:inline-block"
                        />
                        <Link
                            to="/admin/login"
                            className="font-body text-[12px] text-brand-offwhite/50 transition-colors hover:text-brand-offwhite/80"
                            data-testid="footer-admin-link"
                        >
                            Admin
                        </Link>
                    </div>
                </div>
            </div>

            <div className="pointer-events-none absolute -bottom-24 -right-16 select-none font-display text-[240px] leading-none text-brand-offwhite/[0.04] md:text-[380px]">
                EB · Trust
            </div>
        </footer>
    );
}
